import React, { forwardRef, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FaPrint, FaRegFileAlt } from 'react-icons/fa'

import CustomButton from '../../../shared/CustomButton'
import ReusableModal from '../../../shared/ReuseableModal'
import { handlePrintData } from '../../../utils/PrintData'

const firstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '')

const normalizeAdmissionData = value => {
  if (Array.isArray(value)) return value[0] || {}
  return (
    value?.data?.encounter ||
    value?.data?.data?.encounter ||
    value?.encounter ||
    value?.data?.data ||
    value?.data ||
    value ||
    {}
  )
}

const valueText = value => {
  if (Array.isArray(value)) {
    return value.map(valueText).filter(Boolean).join(', ')
  }
  if (typeof value === 'object' && value !== null) {
    return firstValue(
      value.name_en,
      value.name,
      value.title,
      value.label,
      value.display,
      value.description
    )
  }
  return value || ''
}

const PrintLine = ({ label, value }) => (
  <div className='btr-line'>
    <span className='btr-line-label'>{label}</span>
    <span className='btr-line-value'>{valueText(value)}</span>
  </div>
)

const PrintCheck = ({ label, checked }) => (
  <span className='btr-check'>
    <span className='btr-check-box'>{checked ? '\u2713' : ''}</span>
    <span>{label}</span>
  </span>
)

const PrintTitle = ({ children }) => (
  <div className='btr-section-title'>{children}</div>
)

const PrintPanel = ({ children, className = '' }) => (
  <div className={`btr-panel ${className}`}>{children}</div>
)

const PrintBox = ({ value, lines = 2 }) => (
  <div className='btr-write-box' style={{ minHeight: `${lines * 15}px` }}>
    {valueText(value)}
  </div>
)

const getBusinessLogo = businessInfo => {
  const logo = businessInfo?.logo || businessInfo?.business_logo
  if (!logo) return ''
  return String(logo).startsWith('data:')
    ? logo
    : `data:image/png;base64,${logo}`
}

const getBusinessName = businessInfo =>
  firstValue(
    businessInfo?.name_en,
    businessInfo?.business_name,
    businessInfo?.name,
    'Hospital'
  )

const printedDate = () => new Date().toLocaleDateString('en-GB')

const htmlToText = value =>
  String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim()

const printDateTime = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return valueText(value)
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getWardRoomBed = admission => {
  const assignment =
    admission?.active_bed_assignment ||
    admission?.current_bed_assignment ||
    admission?.bed_assignment ||
    admission?.current_assignment ||
    {}
  const bed =
    assignment?.bed ||
    admission?.bed ||
    admission?.assigned_bed ||
    admission?.current_bed ||
    {}
  const ward =
    bed?.room?.ward?.ward_name ||
    bed?.room?.ward?.name_en ||
    bed?.ward?.ward_name ||
    bed?.ward?.name ||
    admission?.ward?.ward_name ||
    admission?.ward?.name ||
    admission?.ward_name
  const room =
    bed?.room?.room_number ||
    bed?.room_number ||
    admission?.room_number ||
    admission?.current_room?.room_number
  const bedNumber =
    bed?.bed_number ||
    bed?.bed_name ||
    bed?.name ||
    admission?.bed_number ||
    admission?.bed_name

  return [ward, room, bedNumber].filter(Boolean).join(' / ')
}

const getDoctors = admission =>
  (admission?.participants || [])
    .map(item => item?.participant_name || item?.practitioner?.name)
    .filter(Boolean)
    .join(', ')

const objectSummary = value => {
  if (!value || typeof value !== 'object') return ''
  return Object.entries(value)
    .filter(([, item]) => item !== undefined && item !== null && item !== '')
    .map(([key, item]) => `${key}: ${valueText(item)}`)
    .join(', ')
}

const getPatientInfo = admission => {
  const patient = admission?.patient || admission?.beneficiary || {}
  return {
    name: patient?.full_name || admission?.patient_name || '',
    mrn: patient?.file_no || patient?.patient_id || admission?.file_no || '',
    patientId: patient?.patient_id || '',
    outpatientId: patient?.out_patient_id || '',
    documentId: patient?.document_id || admission?.document_id || '',
    age: patient?.age || '',
    sex: patient?.gender || patient?.sex || '',
    nationality: patient?.nationality || '',
    beneficiaryType: patient?.beneficiary_type || ''
  }
}

const getSelectedPlan = admission =>
  admission?.patient?.selected_plan || admission?.beneficiary?.selected_plan || {}

const getParticipantRows = admission =>
  (admission?.participants || []).map(participant => ({
    name: participant?.participant_name || participant?.practitioner?.name,
    type: participant?.participant_type,
    role: participant?.role || participant?.practitioner?.role,
    license: participant?.practitioner?.license,
    speciality:
      participant?.practitioner?.speciality?.name_en ||
      participant?.practitioner?.speciality?.name ||
      admission?.speciality?.name_en ||
      admission?.speciality?.name,
    department:
      participant?.practitioner?.department?.name_en ||
      participant?.practitioner?.department?.name ||
      admission?.department?.name_en ||
      admission?.department?.name,
    startTime: printDateTime(participant?.start_time),
    endTime: printDateTime(participant?.end_time)
  }))

const statusValue = admission =>
  String(admission?.admission_status || admission?.status || '').toLowerCase()

const PatientHeader = ({ patient, businessInfo }) => {
  const businessName = getBusinessName(businessInfo)
  const businessLogo = getBusinessLogo(businessInfo)

  return (
    <table className='btr-patient-table'>
      <tbody>
        <tr>
          <td className='btr-business-cell' rowSpan='4'>
            {businessLogo ? (
              <img src={businessLogo} alt={businessName} className='btr-logo' />
            ) : (
              <div className='btr-logo-placeholder'>LOGO</div>
            )}
            <div className='btr-business-name'>{businessName}</div>
          </td>
          <th className='btr-patient-title' colSpan='6'>
            Patient Information
          </th>
        </tr>
        <tr>
          <th className='btr-info-label'>Patient Name</th>
          <td className='btr-info-value' colSpan='5'>
            {valueText(patient.name)}
          </td>
        </tr>
        <tr>
          <th className='btr-info-label'>MRN</th>
          <td className='btr-info-value'>{valueText(patient.mrn)}</td>
          <th className='btr-info-label'>Nationality</th>
          <td className='btr-info-value' colSpan='3'>
            {valueText(patient.nationality)}
          </td>
        </tr>
        <tr>
          <th className='btr-info-label'>Age</th>
          <td className='btr-info-value'>{valueText(patient.age)}</td>
          <th className='btr-info-label'>Sex</th>
          <td className='btr-info-value'>{valueText(patient.sex)}</td>
          <th className='btr-info-label'>Printed</th>
          <td className='btr-info-value'>{printedDate()}</td>
        </tr>
      </tbody>
    </table>
  )
}

const AdmissionPrintContent = ({ admission: admissionData = {}, businessInfo = {} }) => {
  const admission = normalizeAdmissionData(admissionData)
  const patient = getPatientInfo(admission)
  const selectedPlan = getSelectedPlan(admission)
  const status = statusValue(admission)
  const participantRows = getParticipantRows(admission)

  return (
    <div className='btr-print text-gray-950'>
      <style>
        {`
          .btr-print { --btr-border: #111; color: #111; font-family: Arial, sans-serif; font-size: 11px; line-height: 1.18; }
          .btr-sheet { position: relative; border: 1.4px solid var(--btr-border); padding: 7px; background: #fff; }
          .btr-patient-table, .btr-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .btr-patient-table { margin-bottom: 4px; font-size: 10px; line-height: 1.12; }
          .btr-patient-table th, .btr-patient-table td, .btr-table th, .btr-table td { border: 1px solid var(--btr-border); vertical-align: top; word-break: break-word; }
          .btr-patient-table th, .btr-patient-table td { padding: 3px 6px; vertical-align: middle; }
          .btr-business-cell { width: 160px; text-align: center; }
          .btr-logo { display: block; max-height: 54px; max-width: 82px; object-fit: contain; margin: 0 auto 6px; }
          .btr-logo-placeholder { display: flex; width: 52px; height: 52px; align-items: center; justify-content: center; border: 1px solid var(--btr-border); margin: 0 auto 6px; font-size: 9px; font-weight: 800; }
          .btr-business-name { font-size: 11px; font-weight: 800; line-height: 1.15; text-transform: uppercase; }
          .btr-patient-title, .btr-table th, .btr-section-title { background: #edf2f6; font-weight: 800; text-transform: uppercase; }
          .btr-patient-title { font-size: 10px; text-align: left; }
          .btr-info-label { width: 94px; background: #f7f7f7; font-weight: 800; text-align: left; white-space: nowrap; }
          .btr-info-value { font-weight: 700; text-align: left; }
          .btr-panel { border: 1px solid var(--btr-border); border-top: 0; padding: 3px 5px; }
          .btr-section-title { margin-top: 3px; border: 1px solid var(--btr-border); padding: 2px 5px; text-align: center; font-size: 11px; line-height: 1.1; }
          .btr-line { display: flex; min-height: 15px; align-items: flex-end; gap: 4px; font-size: 11px; line-height: 1.08; }
          .btr-line-label { flex-shrink: 0; font-weight: 700; }
          .btr-line-value { min-height: 12px; flex: 1; border-bottom: 1px solid var(--btr-border); padding: 0 2px; }
          .btr-check { display: inline-flex; align-items: center; gap: 2px; padding-right: 6px; font-size: 11px; line-height: 1.05; }
          .btr-check-box { display: inline-flex; width: 8px; height: 8px; flex-shrink: 0; align-items: center; justify-content: center; border: 1px solid var(--btr-border); font-size: 9px; font-weight: 800; line-height: 1; }
          .btr-write-box { border: 1px solid var(--btr-border); padding: 2px; white-space: pre-wrap; font-size: 11px; }
          .btr-table { font-size: 11px; line-height: 1.05; }
          .btr-table th, .btr-table td { padding: 1px 2px; }
          .btr-table th { text-align: center; }
          .btr-footer { display: flex; justify-content: space-between; padding-top: 5px; font-size: 11px; font-weight: 700; }
          @media print { .btr-print { font-size: 11px !important; } }
        `}
      </style>

      <div className='clinical-print-page btr-sheet'>
        <PatientHeader patient={patient} businessInfo={businessInfo} />

        <PrintTitle>Admission Details</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Encounter No.' value={admission?.encounter_number} />
            <PrintLine label='Episode of Care' value={admission?.episode_of_care_id} />
            <PrintLine label='Class' value={admission?.class} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Encounter Status' value={admission?.status} />
            <PrintLine label='Encounter Type' value={admission?.type} />
            <PrintLine label='Resource Type' value={admission?.resource_type} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Priority' value={admission?.priority} />
            <PrintLine label='Care Level' value={admission?.required_care_level} />
            <PrintLine label='Service Type' value={admission?.service_type} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Service Type Code' value={admission?.service_type_code} />
            <PrintLine label='Newborn' value={admission?.is_new_born ? 'Yes' : 'No'} />
            <PrintLine label='Invoice Numbers' value={objectSummary(admission?.invoice_numbers)} />
          </div>
          <div className='flex flex-wrap gap-2 pt-1'>
            <span className='font-semibold'>Admission Status</span>
            <PrintCheck label='Pending' checked={status === 'pending'} />
            <PrintCheck label='Approved' checked={status === 'approved'} />
            <PrintCheck label='Rejected' checked={status === 'rejected'} />
          </div>
        </PrintPanel>

        <PrintTitle>Patient / Coverage Details</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Patient ID' value={patient.patientId} />
            <PrintLine label='Out Patient ID' value={patient.outpatientId} />
            <PrintLine label='Document ID' value={patient.documentId} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Beneficiary Type' value={patient.beneficiaryType} />
            <PrintLine label='Payer Name' value={selectedPlan?.payer_name} />
            <PrintLine label='Policy Number' value={selectedPlan?.policy_number} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Member Card ID' value={selectedPlan?.member_card_id} />
            <PrintLine label='Coverage Type' value={selectedPlan?.coverage_type} />
            <PrintLine label='Policy Holder' value={selectedPlan?.policy_holder} />
          </div>
        </PrintPanel>

        <PrintTitle>Clinical Details</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Department' value={admission?.department?.name_en || admission?.department?.name} />
            <PrintLine label='Speciality' value={admission?.speciality?.name_en || admission?.speciality?.name} />
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Reason Code' value={admission?.reason_code} />
            <PrintLine label='Reason Text' value={admission?.reason_text} />
          </div>
          <PrintLine label='Doctor / Practitioner' value={getDoctors(admission)} />
          <div className='pt-1'>
            <div className='mb-1 font-bold'>Chief Complaint</div>
            <PrintBox value={htmlToText(admission?.chief_complaint)} lines={4} />
          </div>
        </PrintPanel>

        <PrintTitle>Stay / Bed Details</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Start Time' value={printDateTime(admission?.start_time)} />
            <PrintLine label='End Time' value={printDateTime(admission?.end_time)} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Admit Source' value={admission?.admission?.admit_source} />
            <PrintLine label='Admit Source Code' value={admission?.admission?.admit_source_code} />
            <PrintLine label='Pre Admission ID' value={admission?.admission?.pre_admission_id} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Re Admission' value={admission?.admission?.re_admission} />
            <PrintLine label='Intended Length of Stay' value={admission?.admission?.intended_length_of_stay} />
            <PrintLine label='Discharge Disposition' value={admission?.admission?.discharge_disposition} />
          </div>
          <div className='grid grid-cols-3 gap-2'>
            <PrintLine label='Admission Speciality' value={admission?.admission?.admission_speciality} />
            <PrintLine label='Discharge Speciality' value={admission?.admission?.discharge_speciality} />
            <PrintLine label='Ward / Room / Bed' value={getWardRoomBed(admission)} />
          </div>
        </PrintPanel>

        <PrintTitle>Practitioner Details</PrintTitle>
        <PrintPanel>
          {participantRows.length ? (
            <table className='btr-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Role</th>
                  <th>License</th>
                  <th>Speciality</th>
                  <th>Department</th>
                </tr>
              </thead>
              <tbody>
                {participantRows.map((participant, index) => (
                  <tr key={index}>
                    <td>{valueText(participant.name)}</td>
                    <td>{valueText(participant.type)}</td>
                    <td>{valueText(participant.role)}</td>
                    <td>{valueText(participant.license)}</td>
                    <td>{valueText(participant.speciality)}</td>
                    <td>{valueText(participant.department)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <PrintBox value='' lines={2} />
          )}
        </PrintPanel>

        <PrintTitle>Diagnosis Details</PrintTitle>
        <PrintPanel>
          {(admission?.diagnoses || []).length ? (
            <table className='btr-table'>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Diagnosis</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {(admission?.diagnoses || []).map((diagnosis, index) => (
                  <tr key={index}>
                    <td>{valueText(diagnosis?.diagnosis_code)}</td>
                    <td>{valueText(diagnosis?.diagnosis_text)}</td>
                    <td>{valueText(diagnosis?.diagnosis_type)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <PrintBox value='' lines={2} />
          )}
        </PrintPanel>

        <PrintTitle>Companion Details</PrintTitle>
        <PrintPanel>
          {(admission?.companions || []).length ? (
            <table className='btr-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>Mobile</th>
                  <th>Document ID</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {(admission?.companions || []).map((companion, index) => (
                  <tr key={index}>
                    <td>{valueText(companion?.name)}</td>
                    <td>{valueText(companion?.relationship)}</td>
                    <td>{valueText(companion?.mobile)}</td>
                    <td>{valueText(companion?.document_id)}</td>
                    <td>{valueText(companion?.address)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <PrintBox value='' lines={2} />
          )}
        </PrintPanel>

        <PrintTitle>Record Audit</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Created By' value={admission?.created_by} />
            <PrintLine label='Created At' value={printDateTime(admission?.created_at)} />
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Updated By' value={admission?.updated_by} />
            <PrintLine label='Updated At' value={printDateTime(admission?.updated_at)} />
          </div>
        </PrintPanel>

        <PrintTitle>Approval Details</PrintTitle>
        <PrintPanel>
          <div className='grid grid-cols-2 gap-2'>
            <PrintLine label='Approved By' value={admission?.approved_by} />
            <PrintLine label='Approved At' value={printDateTime(admission?.approved_at)} />
          </div>
        </PrintPanel>

        <div className='btr-footer'>
          <span>Admitted Patient Print</span>
          <span>Page 1 of 1</span>
        </div>
      </div>
    </div>
  )
}

const PrintTemplate = forwardRef(({ admission = {}, businessInfo = {} }, ref) => (
  <div ref={ref}>
    <AdmissionPrintContent admission={admission} businessInfo={businessInfo} />
  </div>
))

PrintTemplate.displayName = 'PrintTemplate'

const AdmissionPrint = ({ visible, admission, onClose }) => {
  const { t } = useTranslation()
  const businessInfo = useSelector(state => state?.auth?.buisnessInfo)
  const printRef = useRef(null)

  const handlePrint = async () => {
    if (!printRef.current) return
    await handlePrintData(printRef.current.innerHTML, 'Admitted Patient Print')
  }

  return (
    <ReusableModal
      isVisible={visible}
      onClose={onClose}
      title={t('Admitted Patient Print')}
      HeaderIcon={FaRegFileAlt}
      className='w-full md:w-[760px] lg:w-[920px] xl:w-[1080px]'
      position='top'
      footerButtons={
        <div className='flex w-full items-center justify-end gap-2'>
          <CustomButton
            label={t('Close')}
            buttonType='secondary'
            className='!h-8 !px-4 !text-[12px]'
            onClick={onClose}
          />
          <CustomButton
            label={t('Print')}
            icon={<FaPrint size={12} />}
            buttonType='primary'
            className='!h-8 !px-4 !text-[12px]'
            onClick={handlePrint}
          />
        </div>
      }
    >
      <PrintTemplate
        ref={printRef}
        admission={admission || {}}
        businessInfo={businessInfo || {}}
      />
    </ReusableModal>
  )
}

export default AdmissionPrint
